import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { PublicFooter, PrivacyConsent } from "@/components/PublicFooter";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("register") ? "register" : "login");
  const source = new URLSearchParams(window.location.search).get("source");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #FFF6F3 0%, #FFEDE9 55%, #FFE2DC 100%)" }}>
        <div className="relative z-10">
          <BrandLogo />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="w-16 h-1.5 rounded-full bg-coral mb-8" />
          <h1 className="font-display font-extrabold text-5xl leading-[1.1] mb-6 text-ink">
            La qualité éditoriale, pas le volume.
          </h1>
          <p className="text-ink-light text-lg leading-relaxed">
            Rédigez des posts LinkedIn dans votre voix avec l'IA, créez des visuels à votre image, puis planifiez leur publication.
          </p>
        </div>
        <p className="text-ink-light/60 text-xs relative z-10">
          Pulse, <Link to="/apropos" className="hover:text-coral-dark transition-colors">by APOLLO</Link>
        </p>
        <div className="absolute -right-32 -bottom-32 w-[28rem] h-[28rem] rounded-full bg-coral/15 blur-3xl" />
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <BrandLogo />
          </div>
          <h2 className="font-display font-bold text-3xl text-ink mb-1">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h2>
          <p className="text-slate-500 text-sm mb-8">
            {mode === "login" ? "Accédez à votre studio éditorial." : "Rejoignez votre studio éditorial."}
          </p>
          {mode === "register" && source === "apollo" && (
            <p className="-mt-4 mb-6 text-sm bg-coral-soft/60 border border-coral/20 text-coral-dark rounded-sm px-3.5 py-2.5" data-testid="register-apollo-hint">
              Membres APOLLO : inscrivez-vous avec l'adresse e-mail de votre compte APOLLO.
            </p>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nom</label>
                <input
                  data-testid="register-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors bg-white"
                  placeholder="Gilles Martin"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">E-mail</label>
              <input
                data-testid="email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors bg-white"
                placeholder="vous@exemple.fr"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Mot de passe</label>
              <input
                data-testid="password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors bg-white"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="submit-auth-btn"
              className="w-full bg-coral-strong text-white py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60"
            >
              {busy ? "Patientez…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
            {mode === "register" && <PrivacyConsent />}
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            {mode === "login" ? "Pas encore de compte ? " : "Déjà inscrit ? "}
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-coral-dark font-semibold hover:underline"
            >
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
    <PublicFooter />
    </>
  );
}
